import { useEffect } from 'react';

interface SEOPageMetaProps {
  title: string;
  description: string;
  path: string;
  keywords?: string[];
  ogImage?: string;
}

/**
 * Reusable SEO Meta component for all pages
 * Sets title, description, canonical URL, and Open Graph tags
 */
export const SEOPageMeta = ({ title, description, path, keywords, ogImage }: SEOPageMetaProps) => {
  useEffect(() => {
    // Update document title
    const originalTitle = document.title;
    document.title = `${title} | Scholz Interim`;

    // Update or create meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    const originalDescription = metaDescription?.getAttribute('content') || '';
    
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'description';
      newMeta.content = description;
      document.head.appendChild(newMeta);
    }

    // Update keywords if provided
    if (keywords && keywords.length > 0) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords.join(', '));
      } else {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        metaKeywords.setAttribute('content', keywords.join(', '));
        document.head.appendChild(metaKeywords);
      }
    }

    // Update canonical URL
    const canonicalUrl = `https://scholz-interim.de${path}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    const originalCanonical = canonical?.href || '';
    
    if (canonical) {
      canonical.href = canonicalUrl;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = canonicalUrl;
      document.head.appendChild(canonical);
    }

    // Helper function to add or update meta properties
    const addOrUpdateMetaProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // Update Open Graph tags
    addOrUpdateMetaProperty('og:title', title);
    addOrUpdateMetaProperty('og:description', description);
    addOrUpdateMetaProperty('og:url', canonicalUrl);
    addOrUpdateMetaProperty('og:type', 'website');
    addOrUpdateMetaProperty('og:site_name', 'Scholz Interim');
    addOrUpdateMetaProperty('og:locale', 'de_DE');
    
    if (ogImage) {
      addOrUpdateMetaProperty('og:image', ogImage);
    }

    // Update Twitter Card tags
    const addOrUpdateMetaName = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    addOrUpdateMetaName('twitter:card', 'summary_large_image');
    addOrUpdateMetaName('twitter:title', title);
    addOrUpdateMetaName('twitter:description', description);
    if (ogImage) {
      addOrUpdateMetaName('twitter:image', ogImage);
    }

    // Cleanup function to restore original values
    return () => {
      document.title = originalTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', originalDescription);
      }
      if (canonical && originalCanonical) {
        canonical.href = originalCanonical;
      }
    };
  }, [title, description, path, keywords, ogImage]);

  return null;
};
